(() => {
  const ROOT_SELECTOR = 'product-info.avl-product-page';

  const safeParse = (text, fallback = {}) => {
    try {
      return JSON.parse(text || '');
    } catch (error) {
      console.warn('Avonlea product data could not be parsed.', error);
      return fallback;
    }
  };

  const replaceTokens = (template, values) => {
    return String(template || '')
      .replaceAll('[PRODUCT]', values.product || '')
      .replaceAll('[SKU]', values.sku || 'Not listed')
      .replaceAll('[URL]', values.url || window.location.href);
  };

  const normalize = (value) => String(value || '').trim().toLowerCase();
  const getSectionId = (root) => root?.dataset?.section || '';
  const getConfigurationComponent = (root) => root?.querySelector('[data-avl-configuration]') || null;
  const getEngravingComponent = (root) => root?.querySelector('[data-avl-engraving]') || null;

  const getSelectedSku = (root) => {
    const sectionId = getSectionId(root);
    const skuNode = root.querySelector(`#Sku-${CSS.escape(sectionId)}`) || root.querySelector('[data-avl-sku-text]');
    const sku = skuNode?.textContent?.trim();
    return sku || root.dataset.avlInitialSku || root.dataset.avlMasterStyle || 'Not listed';
  };

  const formatSizeNumber = (number) => {
    const rounded = Math.round((Number(number) + Number.EPSILON) * 1000) / 1000;
    return String(rounded).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
  };

  const expandSizeToken = (rawToken) => {
    const token = String(rawToken || '')
      .trim()
      .replace(/[–—]/g, '-');
    if (!token) return [];

    const rangeMatch = token.match(/^(-?\d+(?:\.\d+)?)\s*-\s*(-?\d+(?:\.\d+)?)(?:\s*@\s*(\d+(?:\.\d+)?))?$/);
    if (!rangeMatch) return [token];

    const start = Number(rangeMatch[1]);
    const end = Number(rangeMatch[2]);
    const step = Number(rangeMatch[3] || 0.5);
    if (!Number.isFinite(start) || !Number.isFinite(end) || !Number.isFinite(step) || step <= 0 || end < start) {
      return [];
    }

    const values = [];
    for (let value = start, guard = 0; value <= end + step / 100 && guard < 200; value += step, guard += 1) {
      values.push(formatSizeNumber(value));
    }
    return values;
  };

  const parseSizeMap = (rawMap) => {
    const entries = [];
    const errors = [];
    const seen = new Set();

    String(rawMap || '')
      .split('|')
      .map((group) => group.trim())
      .filter(Boolean)
      .forEach((group) => {
        const equalsIndex = group.indexOf('=');
        const sizeSpec = (equalsIndex >= 0 ? group.slice(0, equalsIndex) : group).trim();
        const tier = (equalsIndex >= 0 ? group.slice(equalsIndex + 1) : '').trim();
        const sizes = sizeSpec
          .split(',')
          .flatMap(expandSizeToken)
          .map((size) => String(size).trim())
          .filter(Boolean);

        if (!sizes.length) {
          errors.push(`No valid sizes were found in “${group}”.`);
          return;
        }

        sizes.forEach((size) => {
          const key = normalize(size);
          if (seen.has(key)) {
            errors.push(`Finger size ${size} is listed more than once.`);
            return;
          }
          seen.add(key);
          entries.push({ size, tier });
        });
      });

    return { entries, errors };
  };

  const formatCurrency = (cents, currencyCode) => {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return '';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currencyCode || 'USD',
      }).format(amount / 100);
    } catch (error) {
      return `$${(amount / 100).toFixed(2)}`;
    }
  };

  const setConfigurationError = (component, message = '') => {
    if (!component) return;
    const errorNode = component.querySelector('[data-avl-configuration-error]');
    if (errorNode) {
      errorNode.textContent = message;
      errorNode.hidden = !message;
    }
    component.classList.toggle('avl-configuration--error', Boolean(message));
  };

  const getVisibleVariantOptions = (root) => {
    const component = getConfigurationComponent(root);
    const hiddenOptionName = normalize(component?.dataset.sizeUsesTiers === 'true' ? component.dataset.sizeOptionName : '');
    const variantSelects = root?.querySelector('variant-selects');
    if (!variantSelects) return [];

    const items = [];
    variantSelects.querySelectorAll('fieldset, .product-form__input--dropdown').forEach((group) => {
      if (group.hidden || group.classList.contains('avl-internal-variant-option')) return;
      const selected = group.querySelector('input:checked') || group.querySelector('select')?.selectedOptions?.[0];
      if (!selected) return;

      const name = selected.dataset.optionName || group.querySelector('.form__label')?.textContent?.replace(/:$/, '').trim() || '';
      const value = String(selected.value || '').trim();
      if (!name || !value || normalize(name) === hiddenOptionName || normalize(value) === 'default title') return;
      items.push({ name, value });
    });
    return items;
  };

  const getEngravingSelection = (root) => {
    const component = getEngravingComponent(root);
    const toggle = component?.querySelector('[data-avl-engraving-toggle]');
    const lines = component
      ? Array.from(component.querySelectorAll('[data-avl-engraving-line]')).map((input) => input.value.trim().slice(0, 10))
      : [];
    const feeText = component?.querySelector('.avl-engraving__price')?.textContent?.trim() || '+$10.00';

    return {
      component,
      enabled: Boolean(toggle?.checked),
      lines,
      feeText,
      confirmation: component?.querySelector('[data-avl-engraving-confirm]') || null,
    };
  };

  const getConfigurationSelection = (root) => {
    const component = getConfigurationComponent(root);
    if (!component) return { items: [], fingerSize: '', masterStyle: '', complete: true };

    const items = getVisibleVariantOptions(root);
    const sizeSelect = component.querySelector('[data-avl-ring-size]');
    const fingerSize = sizeSelect?.value?.trim() || '';
    if (sizeSelect) {
      items.push({ name: 'Finger Size', value: fingerSize || 'Select a size', pending: !fingerSize });
    }

    return {
      items,
      fingerSize,
      masterStyle: component.dataset.masterStyle || root.dataset.avlMasterStyle || '',
      complete: !sizeSelect || Boolean(fingerSize),
    };
  };

  const appendSummaryRow = (list, item) => {
    const row = document.createElement('div');
    row.className = 'avl-configuration__item';
    if (item.pending) row.classList.add('avl-configuration__item--pending');

    const term = document.createElement('dt');
    term.textContent = item.name;
    const description = document.createElement('dd');
    description.textContent = item.value;

    row.append(term, description);
    list.append(row);
  };

  const updateInquiryForRoot = (root) => {
    const modal = document.getElementById(`AvlInquiryModal-${getSectionId(root)}`);
    modal?.avlUpdateForVariant?.();
  };

  const updateConfigurationSummary = (root) => {
    const component = getConfigurationComponent(root);
    if (!component) return;

    const list = component.querySelector('[data-avl-selection-items]');
    if (list) {
      list.replaceChildren();
      const selection = getConfigurationSelection(root);
      selection.items.forEach((item) => appendSummaryRow(list, item));

      if (component.dataset.engravingAvailable === 'true') {
        const engraving = getEngravingSelection(root);
        appendSummaryRow(list, {
          name: 'Engraving',
          value: engraving.enabled ? `Added (${engraving.feeText})` : 'Not added',
        });
      }

      if (!list.children.length) appendSummaryRow(list, { name: 'Options', value: 'No selections required' });
    }

    const priceNode = component.querySelector('[data-avl-selection-price]');
    if (!priceNode) return;

    if (component.dataset.priceRequest === 'true') {
      priceNode.textContent = component.dataset.priceRequestLabel || 'Price available upon request';
      return;
    }

    if (component.dataset.currentVariantAvailable === 'false') {
      priceNode.textContent = 'Current selection unavailable';
      return;
    }

    const basePrice = Number(component.dataset.currentPrice || 0);
    const engraving = getEngravingSelection(root);
    const engravingFee = engraving.enabled ? Number(component.dataset.engravingFee || 0) : 0;
    priceNode.textContent = formatCurrency(basePrice + engravingFee, component.dataset.currencyCode);
  };

  const findTierControl = (root, optionName, tierValue) => {
    const variantSelects = root?.querySelector('variant-selects');
    if (!variantSelects || !optionName || !tierValue) return null;

    const matchingValue = Array.from(variantSelects.querySelectorAll('[data-option-name]')).find(
      (node) => normalize(node.dataset.optionName) === normalize(optionName) && normalize(node.value) === normalize(tierValue)
    );
    if (!matchingValue) return null;

    if (matchingValue.tagName === 'OPTION') {
      return { type: 'select', control: matchingValue.closest('select'), option: matchingValue };
    }
    return { type: 'radio', control: matchingValue, option: matchingValue };
  };

  const setConfigurationBusy = (component, busy) => {
    if (!component) return;
    component.dataset.updating = busy ? 'true' : 'false';
    component.toggleAttribute('aria-busy', busy);
    clearTimeout(component._avlBusyTimer);
    if (busy) {
      component._avlBusyTimer = window.setTimeout(() => {
        component.dataset.updating = 'false';
        component.removeAttribute('aria-busy');
      }, 5000);
    }
  };

  const applySizeTier = (root, select) => {
    const component = getConfigurationComponent(root);
    if (!component || !select?.value) return true;
    if (component.dataset.sizeUsesTiers !== 'true') return true;

    const selectedOption = select.selectedOptions?.[0];
    const tier = selectedOption?.dataset?.tier || '';
    const optionName = component.dataset.sizeOptionName || 'Size Tier';
    if (!tier) {
      setConfigurationError(component, `Finger size ${select.value} does not have a price tier.`);
      select.setCustomValidity('This finger size is not fully configured. Please call the store for assistance.');
      return false;
    }

    const match = findTierControl(root, optionName, tier);
    if (!match?.control) {
      const message = `The “${tier}” variant value is missing from the “${optionName}” product option.`;
      setConfigurationError(component, message);
      select.setCustomValidity('This finger size is not fully configured. Please call the store for assistance.');
      return false;
    }

    select.setCustomValidity('');
    setConfigurationError(component, '');

    if (match.type === 'radio') {
      if (match.control.checked) return true;
      match.control.checked = true;
      setConfigurationBusy(component, true);
      match.control.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    if (match.control.value === match.option.value) return true;
    match.control.value = match.option.value;
    setConfigurationBusy(component, true);
    match.control.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  };

  const validateConfiguration = (root) => {
    const component = getConfigurationComponent(root);
    if (!component) return true;

    if (component.dataset.updating === 'true') {
      setConfigurationError(component, 'Your updated price is still loading. Please wait a moment and try again.');
      return false;
    }

    const sizeSelect = component.querySelector('[data-avl-ring-size]');
    if (!sizeSelect) return true;

    if (!sizeSelect.value) {
      const message = 'Select a finger size before continuing.';
      sizeSelect.setCustomValidity(message);
      setConfigurationError(component, message);
      sizeSelect.reportValidity();
      sizeSelect.focus({ preventScroll: false });
      return false;
    }

    sizeSelect.setCustomValidity('');
    if (!applySizeTier(root, sizeSelect)) {
      sizeSelect.reportValidity();
      return false;
    }
    setConfigurationError(component, '');
    return true;
  };

  const initializeConfiguration = (root) => {
    const component = getConfigurationComponent(root);
    if (!component || component.dataset.avlInitialized === 'true') return;
    component.dataset.avlInitialized = 'true';

    const sizeSelect = component.querySelector('[data-avl-ring-size]');
    if (sizeSelect) {
      const parsed = parseSizeMap(component.dataset.sizeMap);
      const placeholder = sizeSelect.dataset.placeholder || 'Select a finger size';
      sizeSelect.replaceChildren(new Option(placeholder, ''));
      parsed.entries.forEach(({ size, tier }) => {
        const option = new Option(size, size);
        option.dataset.tier = tier;
        sizeSelect.add(option);
      });

      if (!parsed.entries.length) {
        sizeSelect.disabled = true;
        const message = parsed.errors[0] || 'No ring sizes have been configured for this product.';
        setConfigurationError(component, message);
      } else if (parsed.errors.length) {
        setConfigurationError(component, parsed.errors.join(' '));
      }

      sizeSelect.addEventListener('change', () => {
        sizeSelect.setCustomValidity('');
        if (sizeSelect.value) applySizeTier(root, sizeSelect);
        else setConfigurationError(component, '');
        updateConfigurationSummary(root);
        updateInquiryForRoot(root);
      });
    }

    updateConfigurationSummary(root);
  };

  const validateEngraving = (root) => {
    const engraving = getEngravingSelection(root);
    if (!engraving.enabled) return true;

    const lineOne = engraving.component?.querySelector('[data-avl-engraving-line="1"]');
    lineOne?.setCustomValidity(lineOne.value.trim() ? '' : 'Enter at least one character on engraving line 1.');
    engraving.confirmation?.setCustomValidity(
      engraving.confirmation.checked ? '' : 'Confirm that you reviewed the engraving spelling and punctuation.'
    );

    if (!lineOne?.reportValidity()) return false;
    if (!engraving.confirmation?.reportValidity()) return false;
    return true;
  };

  const setQuantityLocked = (root, locked) => {
    const sectionId = getSectionId(root);
    const quantityWrapper = root.querySelector(`#Quantity-Form-${CSS.escape(sectionId)}`);
    if (!quantityWrapper) return;

    const input = quantityWrapper.querySelector('.quantity__input');
    const buttons = quantityWrapper.querySelectorAll('.quantity__button');

    if (locked) {
      if (input && !input.dataset.avlPreviousQuantity) input.dataset.avlPreviousQuantity = input.value || '1';
      if (input) {
        input.value = '1';
        input.disabled = true;
      }
      buttons.forEach((button) => {
        button.disabled = true;
      });
      quantityWrapper.classList.add('avl-quantity--engraving-locked');
    } else {
      if (input) {
        input.disabled = false;
        input.value = input.dataset.avlPreviousQuantity || input.value || '1';
        delete input.dataset.avlPreviousQuantity;
      }
      buttons.forEach((button) => {
        button.disabled = false;
      });
      quantityWrapper.classList.remove('avl-quantity--engraving-locked');
    }
  };

  const initializeEngraving = (root) => {
    const component = getEngravingComponent(root);
    if (!component || component.dataset.avlInitialized === 'true') return;
    component.dataset.avlInitialized = 'true';

    const toggle = component.querySelector('[data-avl-engraving-toggle]');
    const fields = component.querySelector('[data-avl-engraving-fields]');
    const lineInputs = Array.from(component.querySelectorAll('[data-avl-engraving-line]'));
    const propertyInputs = Array.from(component.querySelectorAll('[data-avl-engraving-property]'));
    const confirmation = component.querySelector('[data-avl-engraving-confirm]');

    if (!toggle || !fields) return;

    const updateCounter = (input) => {
      const counter = input.closest('.avl-engraving__field')?.querySelector('[data-avl-engraving-count]');
      if (counter) counter.textContent = String(input.value.length);
    };

    const updateState = () => {
      const checked = toggle.checked;
      fields.hidden = !checked;
      toggle.setAttribute('aria-expanded', String(checked));
      propertyInputs.forEach((input) => {
        input.disabled = !checked;
      });

      const lineOne = component.querySelector('[data-avl-engraving-required]');
      if (lineOne) lineOne.required = checked;
      if (confirmation) confirmation.required = checked;

      setQuantityLocked(root, checked && component.dataset.onlinePurchase === 'true');

      if (!checked) {
        lineInputs.forEach((input) => {
          input.value = '';
          input.setCustomValidity('');
          updateCounter(input);
        });
        if (confirmation) {
          confirmation.checked = false;
          confirmation.setCustomValidity('');
        }
      } else {
        window.requestAnimationFrame(() => lineInputs[0]?.focus());
      }

      updateConfigurationSummary(root);
      updateInquiryForRoot(root);
    };

    toggle.addEventListener('change', updateState);
    lineInputs.forEach((input) => {
      updateCounter(input);
      input.addEventListener('input', () => {
        input.value = input.value.slice(0, 10);
        input.setCustomValidity('');
        updateCounter(input);
        updateInquiryForRoot(root);
      });
    });
    confirmation?.addEventListener('change', () => confirmation.setCustomValidity(''));

    updateState();
  };

  const updateStatus = (root, variant) => {
    const status = root.querySelector('[data-avl-product-status]');
    if (!status) return;

    const baseState = root.dataset.avlBaseState || 'available';
    if (baseState === 'special' || baseState === 'unavailable') return;

    const isAvailable = Boolean(variant?.available);
    const nextState = isAvailable ? 'available' : 'variant-unavailable';
    const label = isAvailable ? status.dataset.availableLabel : status.dataset.variantUnavailableLabel;
    const detail = isAvailable ? status.dataset.availableMessage : status.dataset.variantUnavailableMessage;

    status.classList.remove(
      'avl-product-status--available',
      'avl-product-status--special',
      'avl-product-status--unavailable',
      'avl-product-status--variant-unavailable'
    );
    status.classList.add(`avl-product-status--${nextState}`);
    status.dataset.state = nextState;

    const labelNode = status.querySelector('[data-avl-status-label]');
    const detailNode = status.querySelector('[data-avl-status-detail]');
    if (labelNode && label) labelNode.textContent = label;
    if (detailNode && detail) detailNode.textContent = detail;
  };

  const initializeInquiry = (root) => {
    const sectionId = getSectionId(root);
    if (!sectionId) return;

    const modal = document.getElementById(`AvlInquiryModal-${sectionId}`);
    if (!modal || modal.dataset.avlInitialized === 'true') return;
    modal.dataset.avlInitialized = 'true';

    const templatesNode = modal.querySelector('[data-avl-inquiry-templates]');
    const templates = safeParse(templatesNode?.textContent, {});
    const textarea = modal.querySelector('[data-avl-inquiry-message]');
    const inquiryType = modal.querySelector('[data-avl-inquiry-type]');
    const skuInputs = modal.querySelectorAll('[data-avl-inquiry-sku-input]');
    const skuTextNodes = modal.querySelectorAll('[data-avl-inquiry-sku-text]');
    const urlInputs = modal.querySelectorAll('[data-avl-inquiry-url-input]');
    const titleNode = modal.querySelector('[data-avl-inquiry-title]');
    const titleField = modal.querySelector('[data-avl-inquiry-title-input]');
    const heading = modal.querySelector('[data-avl-inquiry-heading]');
    const intro = modal.querySelector('[data-avl-inquiry-intro]');
    const configurationInput = modal.querySelector('[data-avl-inquiry-configuration-input]');
    const fingerSizeInput = modal.querySelector('[data-avl-inquiry-finger-size-input]');
    const masterStyleInput = modal.querySelector('[data-avl-inquiry-master-style-input]');
    const configurationSummary = modal.querySelector('[data-avl-inquiry-configuration-summary]');
    const configurationItems = modal.querySelector('[data-avl-inquiry-configuration-items]');
    const engravingRequested = modal.querySelector('[data-avl-inquiry-engraving-requested]');
    const engravingFeeInput = modal.querySelector('[data-avl-inquiry-engraving-fee]');
    const engravingInputs = modal.querySelectorAll('[data-avl-inquiry-engraving-input]');
    const engravingSummary = modal.querySelector('[data-avl-inquiry-engraving-summary]');
    const engravingSummaryLines = modal.querySelectorAll('[data-avl-inquiry-engraving-line]');

    if (textarea && !textarea.dataset.avlPristine) textarea.dataset.avlPristine = 'true';
    textarea?.addEventListener('input', () => {
      textarea.dataset.avlPristine = 'false';
    });

    const productValues = () => ({
      product: root.dataset.avlProductTitle || document.querySelector('h1')?.textContent?.trim() || '',
      sku: getSelectedSku(root),
      url: window.location.href,
    });

    const updateProductFields = () => {
      const values = productValues();
      skuInputs.forEach((input) => {
        input.value = values.sku;
      });
      skuTextNodes.forEach((node) => {
        node.textContent = values.sku;
      });
      urlInputs.forEach((input) => {
        input.value = values.url;
      });
      if (titleNode) titleNode.textContent = values.product;
      if (titleField) titleField.value = values.product;
    };

    const updateConfigurationFields = () => {
      const selection = getConfigurationSelection(root);
      const completeItems = selection.items.filter((item) => item.value && !item.pending);
      const summaryText = completeItems.map((item) => `${item.name}: ${item.value}`).join(' | ');

      if (configurationInput) configurationInput.value = summaryText;
      if (fingerSizeInput) fingerSizeInput.value = selection.fingerSize;
      if (masterStyleInput) masterStyleInput.value = selection.masterStyle;

      if (configurationItems) {
        configurationItems.replaceChildren();
        completeItems.forEach((item) => appendSummaryRow(configurationItems, item));
      }
      if (configurationSummary) configurationSummary.hidden = completeItems.length === 0;

      if (!completeItems.length) return '';
      return `\n\nSelected configuration:\n${completeItems.map((item) => `${item.name}: ${item.value}`).join('\n')}`;
    };

    if (engravingFeeInput && !engravingFeeInput.dataset.avlFeeValue) {
      engravingFeeInput.dataset.avlFeeValue = engravingFeeInput.value;
    }

    const updateEngravingFields = () => {
      const engraving = getEngravingSelection(root);
      if (engravingRequested) engravingRequested.value = engraving.enabled ? 'Yes' : 'No';
      if (engravingFeeInput) {
        engravingFeeInput.value = engraving.enabled ? engravingFeeInput.dataset.avlFeeValue || '' : '';
      }
      engravingInputs.forEach((input) => {
        const index = Number(input.dataset.avlInquiryEngravingInput || 0) - 1;
        input.value = engraving.enabled ? engraving.lines[index] || '' : '';
      });

      if (engravingSummary) engravingSummary.hidden = !engraving.enabled;
      engravingSummaryLines.forEach((node) => {
        const index = Number(node.dataset.avlInquiryEngravingLine || 0) - 1;
        const value = engraving.lines[index] || '';
        node.textContent = value ? `Line ${index + 1}: ${value}` : '';
        node.hidden = !value;
      });

      if (!engraving.enabled) return '';
      const lineText = engraving.lines
        .map((value, index) => (value ? `Line ${index + 1}: ${value}` : ''))
        .filter(Boolean)
        .join('\n');
      return `\n\nRequested engraving (${engraving.feeText}):\n${lineText}`;
    };

    const setIntent = (intent, forceMessage = false) => {
      const normalizedIntent = ['special', 'viewing', 'similar'].includes(intent) ? intent : 'viewing';
      const values = productValues();

      updateProductFields();
      const configurationText = updateConfigurationFields();
      const engravingText = updateEngravingFields();
      if (inquiryType) inquiryType.value = templates[`${normalizedIntent}Type`] || normalizedIntent;
      if (heading)
        heading.textContent = templates[`${normalizedIntent}Heading`] || templates.viewingHeading || 'Ask About This Piece';
      if (intro) intro.textContent = templates[`${normalizedIntent}Intro`] || templates.viewingIntro || '';

      if (textarea && (forceMessage || textarea.dataset.avlPristine !== 'false')) {
        textarea.value = `${replaceTokens(templates[normalizedIntent], values).trim()}${configurationText}${engravingText}`.trim();
        textarea.dataset.avlPristine = 'true';
      }
    };

    root.querySelectorAll('[data-avl-inquiry-intent]').forEach((trigger) => {
      trigger.addEventListener(
        'click',
        (event) => {
          if (!validateConfiguration(root) || !validateEngraving(root)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
          }
          modal.dataset.avlCurrentIntent = trigger.dataset.avlInquiryIntent || 'viewing';
          setIntent(trigger.dataset.avlInquiryIntent, true);
        },
        true
      );
    });

    modal.avlUpdateForVariant = () => {
      updateProductFields();
      updateConfigurationFields();
      updateEngravingFields();
      if (textarea?.dataset.avlPristine !== 'false') {
        const currentIntent = modal.dataset.avlCurrentIntent || root.dataset.avlDefaultInquiryIntent || 'viewing';
        setIntent(currentIntent, true);
      }
    };

    const defaultIntent = root.dataset.avlDefaultInquiryIntent || 'viewing';
    modal.dataset.avlCurrentIntent = defaultIntent;
    setIntent(defaultIntent, false);

    if (modal.dataset.openOnLoad === 'true' || modal.querySelector('[data-avl-open-on-load]')) {
      window.requestAnimationFrame(() => {
        const opener =
          root.querySelector(`[data-avl-inquiry-intent="${defaultIntent}"]`) || root.querySelector('[data-avl-inquiry-intent]');
        if (typeof modal.show === 'function') modal.show(opener);
      });
    }
  };

  const initializeRoot = (root) => {
    if (!root || root.dataset.avlProductInitialized === 'true') return;
    root.dataset.avlProductInitialized = 'true';
    initializeConfiguration(root);
    initializeEngraving(root);
    initializeInquiry(root);
    updateConfigurationSummary(root);
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initializeRoot);
    if (scope.matches?.(ROOT_SELECTOR)) initializeRoot(scope);
  };

  window.AvonleaProductConfiguration = {
    parseSizeMap,
    getSelectionForRoot: getConfigurationSelection,
    updateSummaryForRoot: updateConfigurationSummary,
    validateForForm(form) {
      const root = form?.closest?.(ROOT_SELECTOR) || form?.closest?.('product-info');
      return validateConfiguration(root);
    },
  };

  document.addEventListener('DOMContentLoaded', () => initializeAll());
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('product-info:loaded', (event) => initializeRoot(event.target));

  const subscribeToVariants = () => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.variantChange)
      return false;

    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const sectionId = data?.sectionId;
      const variant = data?.variant;
      if (!sectionId) return;

      document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
        if (getSectionId(root) !== String(sectionId)) return;
        updateStatus(root, variant);

        root.dataset.avlInitialSku = variant?.sku || root.dataset.avlMasterStyle || 'Not listed';
        const skuMeta = root.querySelector('[data-avl-sku-text]');
        if (skuMeta) skuMeta.textContent = root.dataset.avlInitialSku;

        const productForm = root.querySelector('product-form');
        if (productForm) productForm.dataset.avlProductSku = root.dataset.avlInitialSku;

        const configuration = getConfigurationComponent(root);
        if (configuration) {
          configuration.dataset.currentPrice = variant?.price ?? '';
          configuration.dataset.currentVariantAvailable = variant?.available ? 'true' : 'false';
          setConfigurationBusy(configuration, false);
          updateConfigurationSummary(root);
        }

        const modal = document.getElementById(`AvlInquiryModal-${sectionId}`);
        modal?.avlUpdateForVariant?.();
      });
    });

    return true;
  };

  if (!subscribeToVariants()) {
    window.addEventListener('load', subscribeToVariants, { once: true });
  }
})();
